/**
 * Rechenkern des Durchsatz-Dashboards — reine Funktionen, portiert aus dem ersten
 * HTML-Dashboard (RMNC-DASH/dashboard/template.html), dessen Zahlen gegen die
 * Excel-Auswertung geprüft sind.
 *
 * Durchsatz NC feucht [kg/h] = Auswurfgewicht ÷ Zykluszeit × 3600, je Probe.
 */
import { MONATE } from "./format"

export interface AuswertungsDaten {
  meta: {
    jahre: number[]
    teiljahre: Record<string, number>
    letzte_probe: string | null
    grenzen: Record<string, [number, number]>
    tukey: { k: number; min_n: number }
    quellen: Record<string, number>
    letzter_import: { zeitpunkt: string; dateien: string[]; hinweise: TagebuchHinweis[] } | null
  }
  dims: { typ: string[]; geb: string[]; med: string[]; anf: string[] }
  rows: {
    y: number[]; m: number[]; t: number[]; g: number[]; md: number[]; a: number[]
    z: number[]; aw: number[]; f: (number | null)[]; o: number[]
  }
  qualitaet: Array<{ jahr: number; gesamt: number } & Record<string, number>>
  beispiele: Array<{
    grund: string; datum: string; mischung: string; typ: string; geb: string; med: string
    zyklus: string; aw: number | null; f: number | null; tp: number | null; quelle: string
  }>
}

export interface TagebuchHinweis {
  datei: string; jahr: number | null; dubletten: number; ohne_datum: number; bis: string | null
}

export type Kennwert = "mean" | "median"

export interface Filter {
  jahre: Set<number>
  med: Set<number>
  geb: Set<number>
  anf: Set<number>
  typ: number // -1 = alle
  ausr: boolean
  kw: Kennwert
}

export interface Stat { n: number; tp: number | null; z: number | null; aw: number | null; f: number | null }

export const MED_KURZ: Record<string, string> = { Wasser: "wf", Ethanol: "Eth.", IPA: "IPA", Butanol: "But" }

export function alle(n: number): Set<number> {
  return new Set(Array.from({ length: n }, (_, i) => i))
}

function idxVon(liste: string[], namen: string[]): Set<number> {
  return new Set(namen.map((n) => liste.indexOf(n)).filter((i) => i >= 0))
}

export interface Ansicht {
  id: string
  name: string
  titel: string
  setze: (d: AuswertungsDaten) => Pick<Filter, "med" | "geb" | "anf">
}

/** Die vier Sichten der Excel-Auswertung (Pivots von Thorsten Lesemann). */
export const ANSICHTEN: Ansicht[] = [
  {
    id: "alle", name: "Alle Daten", titel: "Alle Gebinde, Medien und Anfeuchtungen",
    setze: (d) => ({ med: alle(d.dims.med.length), geb: alle(d.dims.geb.length), anf: alle(d.dims.anf.length) }),
  },
  {
    id: "standard", name: "Standardfahrweise",
    titel: "Wie Pivot „ohne 30_kleine“: Anfeuchtung 35 %, Gebinde 1-125 und 3-Export",
    setze: (d) => ({ med: alle(d.dims.med.length), geb: idxVon(d.dims.geb, ["1-125", "3-Export"]), anf: idxVon(d.dims.anf, ["35"]) }),
  },
  {
    id: "wasser", name: "Wasserfeucht",
    titel: "Wie Pivot „wasserfeucht“: Medium Wasser, Gebinde 1-125 und 3-Export",
    setze: (d) => ({ med: idxVon(d.dims.med, ["Wasser"]), geb: idxVon(d.dims.geb, ["1-125", "3-Export"]), anf: alle(d.dims.anf.length) }),
  },
  {
    id: "chips", name: "Chips", titel: "Wie Pivot „Chips“: nur Gebinde 10-Chips",
    setze: (d) => ({ med: alle(d.dims.med.length), geb: idxVon(d.dims.geb, ["10-Chips"]), anf: alle(d.dims.anf.length) }),
  },
]

export function startFilter(d: AuswertungsDaten): Filter {
  return {
    jahre: new Set(d.meta.jahre),
    ...ANSICHTEN[0].setze(d),
    typ: -1,
    ausr: false,
    kw: "mean",
  }
}

export function gleich(a: Set<number>, b: Set<number>): boolean {
  return a.size === b.size && [...a].every((x) => b.has(x))
}

/** Durchsatz je Probe (vorberechnet, einmal je Datensatz). */
export function durchsatzListe(d: AuswertungsDaten): Float64Array {
  const r = d.rows
  const tp = new Float64Array(r.y.length)
  for (let i = 0; i < tp.length; i++) tp[i] = (r.aw[i] / r.z[i]) * 3600
  return tp
}

export function auswahl(
  d: AuswertungsDaten, f: Filter, opt: { ohneTyp?: boolean; nurAusr?: boolean } = {},
): number[] {
  const r = d.rows
  const out: number[] = []
  for (let i = 0; i < r.y.length; i++) {
    if (!f.jahre.has(r.y[i]) || !f.med.has(r.md[i]) || !f.geb.has(r.g[i]) || !f.anf.has(r.a[i])) continue
    if (opt.nurAusr ? !r.o[i] : !f.ausr && r.o[i]) continue
    if (!opt.ohneTyp && f.typ >= 0 && r.t[i] !== f.typ) continue
    out.push(i)
  }
  return out
}

export function kennwert(werte: number[], kw: Kennwert): number | null {
  const n = werte.length
  if (!n) return null
  if (kw === "median") {
    const s = Float64Array.from(werte).sort()
    return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2
  }
  let t = 0
  for (const v of werte) t += v
  return t / n
}

export function stat(d: AuswertungsDaten, tp: Float64Array, ids: number[], kw: Kennwert): Stat {
  const r = d.rows
  const tpw: number[] = [], z: number[] = [], aw: number[] = [], f: number[] = []
  for (const i of ids) {
    tpw.push(tp[i]); z.push(r.z[i]); aw.push(r.aw[i])
    const fi = r.f[i]
    if (fi != null) f.push(fi)
  }
  return { n: ids.length, tp: kennwert(tpw, kw), z: kennwert(z, kw), aw: kennwert(aw, kw), f: kennwert(f, kw) }
}

export function gruppiere<K>(ids: number[], key: (i: number) => K): Map<K, number[]> {
  const m = new Map<K, number[]>()
  for (const i of ids) {
    const k = key(i)
    const g = m.get(k)
    if (g) g.push(i)
    else m.set(k, [i])
  }
  return m
}

export function jahrLabel(d: AuswertungsDaten, y: number): string {
  const bis = d.meta.teiljahre[String(y)]
  return bis ? `${y} bis ${MONATE[bis - 1]}` : String(y)
}

export const kwKurz = (kw: Kennwert) => (kw === "median" ? "Median" : "Mittelwert")
